import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button, Upload, message, Alert, Typography, Radio } from 'antd';
import {
    UploadOutlined,
    BankOutlined,
    FileTextOutlined,
    CopyOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import AuthService from '../../services/authService';

const { Text, Title } = Typography;

const WireTransferModal = ({ visible, onClose, grade, amount }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [prevLicenseUrl, setPrevLicenseUrl] = useState(null);
    const [proofType, setProofType] = useState('tax_invoice');
    const [targetGrade, setTargetGrade] = useState(grade?.grade_name || 'basic');
    const [isPending, setIsPending] = useState(false);
    const [bankName, setBankName] = useState('국민은행');
    const [bankAccount, setBankAccount] = useState('123-456-789012');
    const [bankHolder, setBankHolder] = useState('대표자명');

    useEffect(() => {
        if (visible) {
            setTargetGrade(grade?.grade_name || 'basic');
            fetchLastInfo();
        } else {
            form.resetFields();
            setFileList([]);
            setProofType('tax_invoice');
            setIsPending(false);
        }
    }, [visible, grade]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchLastInfo = async () => {
        const gradeBase = grade?.price || (grade?.grade_name === 'premium' ? 50000 : 30000);
        const vatIncluded = gradeBase + Math.round(gradeBase * 0.1);

        try {
            const res = await AuthService.authAxios.get('/payments/wire-transfer/last-info');
            if (res.data && res.data.success && res.data.data) {
                if (res.data.data.bankName) setBankName(res.data.data.bankName);
                if (res.data.data.bankAccount) setBankAccount(res.data.data.bankAccount);
                if (res.data.data.bankHolder) setBankHolder(res.data.data.bankHolder);

                form.setFieldsValue(res.data.data);
                // 등급 기반 VAT 포함 금액으로 항상 덮어씀
                form.setFieldsValue({ depositAmount: vatIncluded });

                if (res.data.data.status === 'WIRE_TRANSFER_PENDING') {
                    setIsPending(true);
                }

                if (res.data.data.businessLicenseUrl) {
                    const url = process.env.REACT_APP_API_URL + res.data.data.businessLicenseUrl;
                    setPrevLicenseUrl(url);
                    setFileList([
                        {
                            uid: '-1',
                            name: '기존_사업자등록증',
                            status: 'done',
                            url: url,
                            isExisting: true,
                        },
                    ]);
                }
            } else {
                form.setFieldsValue({ depositAmount: vatIncluded });
            }
        } catch (error) {
            console.error('Failed to fetch last info', error);
            form.setFieldsValue({ depositAmount: vatIncluded });
        }
    };

    const GRADE_BASE_PRICE = { basic: 30000, premium: 50000 };
    const basePrice = GRADE_BASE_PRICE[targetGrade] || 30000;
    const vatAmount = Math.round(basePrice * 0.1);
    const totalAmount = basePrice + vatAmount;

    const handleGradeChange = e => {
        const selected = e.target.value;
        setTargetGrade(selected);
        const base = GRADE_BASE_PRICE[selected] || 30000;
        form.setFieldsValue({ depositAmount: base + Math.round(base * 0.1) });
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            if (proofType === 'tax_invoice') {
                if (fileList.length === 0 && !prevLicenseUrl) {
                    message.error('사업자등록증 사본을 첨부해주세요.');
                    return;
                }
            }

            setLoading(true);

            const formData = new FormData();
            formData.append('amount', values.depositAmount || amount);
            formData.append('depositorName', values.depositorName);
            formData.append('email', values.email);
            formData.append('proofType', proofType);
            formData.append('grade', targetGrade);

            if (proofType === 'tax_invoice') {
                formData.append('companyName', values.companyName);
                formData.append('businessNumber', values.businessNumber);
                formData.append('ceoName', values.ceoName);
                formData.append('businessCondition', values.businessCondition);
                formData.append('businessItem', values.businessItem);

                // isExisting 플래그가 없는 새 파일이 업로드된 경우
                const newFile = fileList.find(f => !f.isExisting);

                if (newFile) {
                    formData.append('businessLicense', newFile.originFileObj || newFile);
                } else if (fileList.length > 0 && fileList[0].isExisting) {
                    // 기존 파일을 그대로 유지한 경우
                    formData.append(
                        'existingLicenseUrl',
                        prevLicenseUrl.replace(process.env.REACT_APP_API_URL, '')
                    );
                } else {
                    message.error('사업자등록증 사본을 첨부해주세요.');
                    setLoading(false);
                    return;
                }
            } else if (proofType === 'cash_receipt') {
                formData.append('cashReceiptPhone', values.cashReceiptPhone);
            }

            const response = await AuthService.authAxios.post(
                '/payments/wire-transfer/apply',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.success) {
                message.success(
                    '신청이 성공적으로 접수되었습니다. 입금 확인 후 승인해 드리겠습니다.'
                );
                form.resetFields();
                setFileList([]);
                onClose();
            } else {
                message.error(response.data.message || '신청 중 오류가 발생했습니다.');
            }
        } catch (error) {
            console.error('Wire transfer apply error:', error);
            if (error.errorFields) return; // 유효성 검사 실패 시
            message.error(error.response?.data?.message || '신청 처리 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        onClose();
    };

    const uploadProps = {
        onRemove: _file => {
            setFileList([]);
        },
        beforeUpload: file => {
            const isImageOrPdf = file.type.startsWith('image/') || file.type === 'application/pdf';
            if (!isImageOrPdf) {
                message.error('이미지 또는 PDF 파일만 업로드 가능합니다!');
                return Upload.LIST_IGNORE;
            }
            const isLt20M = file.size / 1024 / 1024 < 20;
            if (!isLt20M) {
                message.error('파일 크기는 20MB 이하여야 합니다!');
                return Upload.LIST_IGNORE;
            }
            setFileList([file]);
            return false;
        },
        fileList,
    };

    const handleProofTypeChange = e => {
        const type = e.target.value;
        setProofType(type);
        if (type !== 'tax_invoice') {
            setFileList([]);
            form.setFieldsValue({
                companyName: undefined,
                businessNumber: undefined,
                ceoName: undefined,
                businessCondition: undefined,
                businessItem: undefined,
            });
        }
        if (type !== 'cash_receipt') {
            form.setFieldsValue({
                cashReceiptPhone: undefined,
            });
        }
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileTextOutlined style={{ color: '#1677ff' }} />
                    <span>무통장 입금 및 입금확인요청</span>
                </div>
            }
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            okText={isPending ? '심사 중 (수정 불가)' : '입금확인요청'}
            cancelText="취소"
            width={500}
            okButtonProps={{ disabled: isPending }}
        >
            {isPending && (
                <Alert
                    message="처리 대기 중"
                    description="현재 관리자가 입금 내역을 확인하고 있습니다. 승인 처리 전까지는 내용을 수정할 수 없으며, 반려(취소)된 경우에만 다시 신청할 수 있습니다."
                    type="warning"
                    showIcon
                    style={{ marginBottom: '16px' }}
                />
            )}

            <div
                style={{
                    padding: '12px 16px',
                    background: '#f8faff',
                    borderRadius: '12px',
                    border: '1px solid #e6f4ff',
                    marginBottom: '16px',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                    }}
                >
                    <div
                        style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #1677ff 0%, #69b1ff 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(22, 119, 255, 0.3)',
                        }}
                    >
                        <BankOutlined style={{ fontSize: '16px', color: '#fff' }} />
                    </div>
                    <Title
                        level={5}
                        style={{
                            margin: 0,
                            fontWeight: 800,
                            color: '#141414',
                            letterSpacing: '-0.5px',
                        }}
                    >
                        무통장 입금 안내
                    </Title>
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '8px',
                        background: '#fff',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #e6f4ff',
                    }}
                >
                    <Text style={{ fontSize: '14px', color: '#595959', fontWeight: 600 }}>
                        {bankName}
                    </Text>
                    <strong
                        style={{
                            fontSize: '18px',
                            color: '#1677ff',
                            fontWeight: 900,
                            fontFamily: 'Consolas, monospace',
                            lineHeight: '1',
                        }}
                    >
                        {bankAccount}
                    </strong>
                    <Text style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        (예금주: {bankHolder})
                    </Text>

                    <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                            navigator.clipboard.writeText(bankAccount);
                            message.success('계좌번호가 복사되었습니다.');
                        }}
                        style={{
                            marginLeft: 'auto',
                            borderRadius: '6px',
                            color: '#1677ff',
                            borderColor: '#91caff',
                            fontWeight: 600,
                        }}
                    >
                        계좌복사
                    </Button>
                </div>

                <div
                    style={{
                        marginTop: '8px',
                        padding: '0 4px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '6px',
                    }}
                >
                    <InfoCircleOutlined style={{ color: '#1677ff', marginTop: '4px' }} />
                    <Text style={{ fontSize: '13px', color: '#595959', lineHeight: '1.4' }}>
                        입금 후 반드시 하단의 <strong>&apos;입금확인요청&apos;</strong> 버튼을
                        클릭해주셔야 입금 확인 처리가 진행됩니다.
                    </Text>
                </div>
            </div>

            <Form form={form} layout="vertical" name="wire_transfer_form" disabled={isPending}>
                <Form.Item label="신청할 요금제" style={{ marginBottom: '16px' }}>
                    <Radio.Group
                        value={targetGrade}
                        onChange={handleGradeChange}
                        buttonStyle="solid"
                    >
                        <Radio.Button value="basic">Basic (30,000원, VAT 별도)</Radio.Button>
                        <Radio.Button value="premium">Premium (50,000원, VAT 별도)</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <Form.Item label="증빙 서류 발급 유형" style={{ marginBottom: '16px' }}>
                    <Radio.Group
                        value={proofType}
                        onChange={handleProofTypeChange}
                        buttonStyle="solid"
                    >
                        <Radio.Button value="tax_invoice">세금계산서 (사업자)</Radio.Button>
                        <Radio.Button value="cash_receipt">현금영수증 (개인)</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#f9f9f9', borderRadius: '8px', fontSize: '13px', color: '#8c8c8c' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>공급가액</span>
                        <span>{basePrice.toLocaleString()}원</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                        <span>VAT (10%)</span>
                        <span>{vatAmount.toLocaleString()}원</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e8e8e8', color: '#262626', fontWeight: 700, fontSize: '14px' }}>
                        <span>최종 입금 금액</span>
                        <span>{totalAmount.toLocaleString()}원</span>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '16px' }}>
                    <Form.Item
                        name="depositorName"
                        label="입금자명"
                        style={{ flex: 1, marginBottom: '16px' }}
                        rules={[{ required: true, message: '실제 입금하실 이름을 입력해주세요.' }]}
                    >
                        <Input placeholder="홍길동" />
                    </Form.Item>

                    <Form.Item
                        name="depositAmount"
                        label="입금(예정) 금액"
                        style={{ flex: 1, marginBottom: '16px' }}
                        rules={[
                            { required: true, message: '실제로 입금하실 금액을 입력해주세요.' },
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={v => v.replace(/,/g, '')}
                            placeholder="예: 55,000"
                            addonAfter="원"
                            min={0}
                        />
                    </Form.Item>
                </div>

                {proofType === 'tax_invoice' && (
                    <>
                        <div style={{ display: 'flex', gap: '16px' }}>
                            <Form.Item
                                name="companyName"
                                label="상호명"
                                style={{ flex: 1, marginBottom: '16px' }}
                                rules={[
                                    {
                                        required: true,
                                        message: '세금계산서에 발행될 상호명을 입력해주세요.',
                                    },
                                ]}
                            >
                                <Input placeholder="티에스애드" />
                            </Form.Item>

                            <Form.Item
                                name="businessNumber"
                                label="사업자등록번호"
                                style={{ flex: 1, marginBottom: '16px' }}
                                rules={[
                                    { required: true, message: '사업자등록번호를 입력해주세요.' },
                                ]}
                            >
                                <Input placeholder="123-45-67890" />
                            </Form.Item>
                        </div>

                        <Form.Item
                            name="ceoName"
                            label="대표자명"
                            style={{ marginBottom: '16px' }}
                            rules={[{ required: true, message: '대표자 성함을 입력해주세요.' }]}
                        >
                            <Input placeholder="김대표" />
                        </Form.Item>

                        <div style={{ display: 'flex', gap: '16px' }}>
                            <Form.Item
                                name="businessCondition"
                                label="업태"
                                style={{ flex: 1, marginBottom: '16px' }}
                                rules={[
                                    {
                                        required: true,
                                        message: '업태를 입력해주세요. (예: 서비스업)',
                                    },
                                ]}
                            >
                                <Input placeholder="서비스업 등" />
                            </Form.Item>

                            <Form.Item
                                name="businessItem"
                                label="종목"
                                style={{ flex: 1, marginBottom: '16px' }}
                                rules={[
                                    {
                                        required: true,
                                        message: '종목을 입력해주세요. (예: 소프트웨어 개발)',
                                    },
                                ]}
                            >
                                <Input placeholder="소프트웨어 개발 등" />
                            </Form.Item>
                        </div>

                        <Form.Item label="사업자등록증 사본 (이미지 또는 PDF)" required>
                            <Upload {...uploadProps} maxCount={1} listType="picture">
                                <Button icon={<UploadOutlined />}>파일 선택</Button>
                            </Upload>
                            <Text
                                type="secondary"
                                style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}
                            >
                                * 20MB 이하의 파일만 업로드 가능합니다. (지원 확장자: JPG, PNG, PDF)
                            </Text>
                        </Form.Item>
                    </>
                )}

                {proofType === 'cash_receipt' && (
                    <Form.Item
                        name="cashReceiptPhone"
                        label="휴대폰 번호 (현금영수증 발행용)"
                        style={{ marginBottom: '16px' }}
                        rules={[
                            {
                                required: true,
                                message: '현금영수증을 발행받을 휴대폰 번호를 입력해주세요.',
                            },
                        ]}
                    >
                        <Input placeholder="010-1234-5678" />
                    </Form.Item>
                )}

                <Form.Item
                    name="email"
                    label="안내 수신 이메일"
                    style={{ marginBottom: '0' }}
                    rules={[
                        { required: true, message: '이메일을 입력해주세요.' },
                        { type: 'email', message: '유효한 이메일 형식이 아닙니다.' },
                    ]}
                >
                    <Input placeholder="tax@example.com" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default WireTransferModal;
